import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service'
import { BaseChartDirective } from 'ng2-charts';
import { ChartLegends, MetricDataValue, LegendDataValues, ComparisonChartColors } from '../qm-evaluator-performance.model';

@Component({
    selector: 'cv-qm-evaluator-performance-comparison',
    templateUrl: './qm-evaluator-performance-comparison.component.html',
    styleUrl: './qm-evaluator-performance-comparison.component.scss'
})

export class QmEvaluatorPerformanceComparisonComponent {
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;
    @Output() chartRef: EventEmitter<any> = new EventEmitter<any>;
    chartConfig = null;
    dataLoaded = false;
    state = null;
    _chartData: MetricDataValue = {
        completed: 0,
        pending: 0,
        appealed: 0,
        deleted: 0,
        replaced: 0,
        expired: 0,
        total: 0,
        appealedExpired: 0,
        completedDeleted: 0
    }
    chartLegends = {
        completed: "Completed",
        pending: "Pending",
        appealed: "Appealed",
        deleted: "Deleted",
        replaced: "Replaced",
        expired: "Expired",
        appealedExpired: "Appealed Expired",
        completedDeleted: "Completed Deleted"
    };
    data: MetricDataValue = {
        total: 0,
        completed: 0,
        pending: 0,
        expired: 0,
        deleted: 0,
        replaced: 0,
        appealed: 0,
        appealedExpired: 0,
        completedDeleted: 0
    };
    _legendDataValues: LegendDataValues = {};
    _legendData: ChartLegends[] = [];
    _chartConstants = {
        colors: [],
        colorsLight: [],
        borderColor: 'rgba(255, 255, 255, 1)', // white
        fontColor: "rgba(0, 0, 0, 1)", // theUpsideDown color
        fontFamily: "'open-sans', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'", // Includes fallbacks (from default ChartJS config)
    };
    fontSize = 12;
    @Input() set chartData(data: MetricDataValue) {
        this._chartData = data;
        this._legendDataValues = {};
        this.updateChartLegendData(this._legendData);
    }

    get chartData(): MetricDataValue {
        return this._chartData;
    }

    @Input() set legendData(data: ChartLegends[]) {
        this._legendData = data;
        this.updateChartLegendData(data);
    }

    @Input() set fetchChartObj(data: any) {
        this.chartRef.emit(this.chartObject)
    }

    get legendData(): ChartLegends[] {
        return this._legendData;
    }

    constructor(private cvLocalizationService: CVLocalizationService,
        private config: CvConfigurationService) {
        for (let key in ComparisonChartColors) {
            this._chartConstants.colors.push(ComparisonChartColors[key]);
            this._chartConstants.colorsLight.push(ComparisonChartColors[key]);
        }
    }

    ngOnInit(): void {
        // Left blank
    }

    async updateChartLegendData(data: ChartLegends[]): Promise<void> {
        if (this._chartData) {
            data.forEach(element => {
                if (element.toggleLegend) {
                    if (!this._legendDataValues.hasOwnProperty(element.key)) {
                        this._legendDataValues[element.key] = this._chartData[element.key];
                    }
                    this._chartData[element.key] = 0;
                } else {
                    if (this._legendDataValues[element.key]) {
                        this._chartData[element.key] = this._legendDataValues[element.key];
                    }
                }
            });
            this._chartData.total = 0;
            for (let key in this._chartData) {
                if (key !== 'total') {
                    this._chartData.total += this._chartData[key];
                }
            }
            // after the legend generated chart creation is invoked
            await this.createChart();
            //After the chart generated dom is made visible
            this.dataLoaded = true;
        }
    }

    async createChart(): Promise<void> {
        this.data = this.chartData;
        // Check for and destroy any existing charts
        if (this.state == null || this.state.legendPosition == null) {
            this.state = {
                legendPosition: 'top',
                dataPadding: '5% 0 0 0'
            };
        }
        const preparedLabels = [];
        for (const key in this.chartLegends) {
            if (this.chartLegends.hasOwnProperty(key)) {
                preparedLabels.push(this.cvLocalizationService.getCachedText('Evaluator_Performance', key, this.chartLegends[key]));
            }
        }
        this.chartConfig =
            {
                type: 'doughnut',
                data: {
                    datasets: [{
                        data: this.mapChartValues(this.data),
                        backgroundColor: this._chartConstants.colors,
                        borderColor: this._chartConstants.borderColor,
                        borderWidth: 1,
                        hoverBackgroundColor: this._chartConstants.colorsLight,
                    }],
                    labels: preparedLabels
                },
                options: {
                    cutout: '60%', // makes ring thicker and thinner 76 makes it 48 px thick
                    responsive: true,
                    maintainAspectRatio: false,
                    elements: {
                        center: {
                            text: "", // The actual content of the center is drawn by the beforeDraw custom plugin below
                            color: this._chartConstants.fontColor,
                            fontFamily: this._chartConstants.fontFamily,
                        },
                    },
                    plugins: {
                        legend: {
                            display: false
                        },
                        emptyChart: {
                            disableCxone: this.config.isCxOne,
                        },
                        emptyDoughnut: this.data.total === 0 ? {
                            color: '#D9D9D9',
                            width: 15,
                            radiusDecrease: 5
                        } : {},
                        tooltip: {
                            enabled: false,
                            mode: 'index',
                            position: 'average',
                            padding: this.config.isCxOne ? 12 : 6,
                            usePointStyle: true,
                            boxPadding: this.config.isCxOne ? 3 : 1,
                            boxWidth: this.config.isCxOne ? 11 : 18,
                            boxHeight: this.config.isCxOne ? 11 : 18,
                            borderColor: this.config.isCxOne ? '#D0D2D3' : '#353a40',
                            titleColor: this.config.isCxOne ? '#000' : '#fff',
                            bodyColor: this.config.isCxOne ? '#353a40' : '#fff',
                            footerColor: '#353a40',
                            backgroundColor: this.config.isCxOne ? '#FFF' : 'rgba(0, 0, 0, 0.8)',
                            labelFont: {
                                size: 12,
                                weight: 'bold'
                            },
                            footerFont: {
                                fontStyle: 'Open Sans',
                                family: 'Arial',
                                weight: 'normal'
                            },
                            borderWidth: this.config.isCxOne ? 1 : 0,
                            cornerRadius: this.config.isCxOne ? 0 : 6,
                            external: (context) => {
                                this.tooltipExternalHandle(context);
                            },
                            callbacks: {
                                labelPointStyle: context => {
                                    const dataset = context.chart.data.datasets[context.datasetIndex];
                                    const sliceColor = dataset.backgroundColor[context.dataIndex];
                                    if (sliceColor) {
                                        return {
                                            pointStyle: 'circle',
                                            pointBackgroundColor: sliceColor,
                                            rotation: 0,
                                        };
                                    }
                                    return;
                                },
                            },
                        },
                        datalabels: {
                            color: ['rgba(0, 0, 0, 1)', 'rgba(255, 255, 255, 1)', 'rgba(0, 0, 0, 1)', 'rgba(255, 255, 255, 1)', 'rgba(255, 255, 255, 1)', 'rgba(0, 0, 0, 1)'],
                            display: (context) => {
                                const dataset = context.dataset;
                                const value = dataset.data[context.dataIndex];
                                const total = (dataset.data as number[]).reduce((sum, data) => sum + (typeof data === 'number' ? data : 0), 0);
                                const percentage = (typeof value === 'number' ? value : 0) / total * 100;
                                return percentage > 5; // Display label only if partition is larger than 10%
                            },
                            formatter: (value) => {
                                const ind = this.chartConfig.data.datasets[0].data.findIndex(a => a == value);
                                if (value === 0) {
                                    return ''; // Return empty string if value is zero
                                } else {
                                    return this.getDataByIndex(ind);
                                }
                            },
                            anchor: 'center',
                            align: 'center',
                            offset: 0,
                            font: (context) => {
                                this.drawCenterLabel(context.chart);
                                return {
                                    size: this.fontSize,
                                    family: 'Open Sans, sans-serif' // Set font family
                                };
                            },
                        },
                    },
                    layout: {
                        padding: {
                            left: 8,
                            right: 8,
                            top: 8,
                            bottom: 8
                        }
                    },
                    animation: {
                        duration: 0,
                    },
                    hover: {
                        animationDuration: 0,
                    },
                },
                plugins: [this.data.total === 0 ? {
                    id: 'emptyDoughnut',
                    beforeDraw: chart => this.drawCenterLabel(chart),
                    afterDraw: (chart, args, options) => {
                        const { color, width, radiusDecrease } = options;
                        const { chartArea: { left, top, right, bottom }, ctx } = chart;
                        const centerX = (left + right) / 2;
                        const centerY = (top + bottom) / 2;
                        const r = Math.min(right - left, bottom - top) / 2;
                        ctx.beginPath();
                        ctx.lineWidth = width || 2;
                        ctx.strokeStyle = '#D9D9D9';
                        ctx?.arc(centerX, centerY, (r - radiusDecrease || 0), 0, 2 * Math.PI);
                        ctx.stroke();
                    }
                } :
                    {
                        id: "coaching-status-center-label",
                        beforeDraw: chart => this.drawCenterLabel(chart),
                    }],
            } as ChartConfiguration;
        this.chartConfig.data.datasets[0].data = this.mapChartValues(this.data);
    }

    mapChartValues(dataObj: MetricDataValue) {
        const values = [];
        for (const key in this.chartLegends) {
            if (dataObj.hasOwnProperty(key)) {
                values.push(dataObj[key]);
            }
        }
        return values;
    }

    drawCenterLabel(chart) {
        if (chart.config.options.elements.center) {
            //Get ctx from string
            const ctx = chart.ctx;

            //Get options from the center object in options
            const centerConfig = chart.config.options.elements.center;
            const fontStyle = centerConfig.fontStyle || this._chartConstants.fontFamily;

            // total the shown data 
            let total = this.data.total;
            const txt = total.toFixed(0); // agent 
            let txt2 = this.cvLocalizationService.getCachedText('Evaluator_Performance', "Total_Evaluations", "Total Evaluations");
            const color = centerConfig.color || this._chartConstants.fontColor;
            const sidePadding = centerConfig.sidePadding || 20;
            const innerRadius = chart._metasets[0].data[0].innerRadius;

            const sidePaddingCalculated = (sidePadding / 100) * (innerRadius * 2);
            //Start with a base font of 30px
            ctx.font = "30px " + fontStyle;

            //Get the width of the string and also the width of the element minus 10 to give it 5px side padding
            const stringWidth2 = ctx.measureText(txt2).width;
            const elementWidth = (innerRadius * 2) - sidePaddingCalculated;

            // Find out how much the font can grow in width.
            const widthRatio2 = elementWidth / stringWidth2;

            const newFontSize = Math.floor(25 * widthRatio2); // using text to determine number size
            const newFontSize2 = Math.floor(newFontSize * 0.7);

            const elementHeight = (innerRadius * 2);

            // Pick a new font size so it will not be larger than the height of label.
            const fontSizeToUse = Math.min(newFontSize, elementHeight) * 2;
            const fontSizeToUse2 = Math.min(newFontSize2, elementHeight) * 1.8;

            // Set font settings to draw it correctly.
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const centerX = ((chart.chartArea.left + chart.chartArea.right) / 2);
            const centerY = ((chart.chartArea.top + chart.chartArea.bottom) / 2);
            ctx.font = fontSizeToUse + "px " + fontStyle;
            ctx.fillStyle = color;

            // Draw number in center
            ctx.fillText(txt, centerX, centerY - (fontSizeToUse2 / 2) - 6);

            ctx.font = fontSizeToUse2 + "px " + fontStyle;

            // Draw Agents text in center
            ctx.fillText(txt2, centerX, centerY + (fontSizeToUse2 / 2) + 6);
            if (this.fontSize !== fontSizeToUse2) {
                this.fontSize = fontSizeToUse2;
            }
        }
    }

    getDataByIndex(index) {
        const legendKeys = Object.keys(this.chartLegends);
        return this.data && this.data[legendKeys[index]] !== undefined ? this.data[legendKeys[index]].toString() : 0;
    }

    tooltipExternalHandle(context) {
        let tooltipEl = document.getElementById('chartjs-tooltip-comparison');
        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip-comparison';
            tooltipEl.innerHTML = '<table></table>';
            document.body.appendChild(tooltipEl);
        }
        const tooltipModel = context.tooltip;
        if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
        }
        if (tooltipModel.body) {
            const dataFromCurrentElement = tooltipModel.dataPoints[0] as any;
            const metricName = dataFromCurrentElement.label;
            const metricval = parseFloat((dataFromCurrentElement.dataset as any).data[dataFromCurrentElement.dataIndex]).toFixed(2);
            let total: number;
            if (Array.isArray((dataFromCurrentElement.dataset as any).data)) {
                total = (dataFromCurrentElement.dataset as any).data.reduce((acc, val) => acc + parseFloat(val), 0);
            } else {
                total = (dataFromCurrentElement.dataset as any).data as number;
            }
            const percentage = (parseFloat(metricval) / total * 100).toFixed(2) + '%';
            const floatPercentage = parseFloat(percentage);
            const floatMetricValue = parseFloat(metricval);
            let percentageFormatted = floatPercentage === Math.floor(floatPercentage) ? Math.floor(floatPercentage) + '%' : percentage;
            const displayValueFormatted = floatMetricValue === Math.floor(floatMetricValue) ? Math.floor(floatMetricValue) : metricval;
            const boldMetricValue = `<span style="font-weight: bold">${displayValueFormatted}</span>`;
            const boldPercentage = `<span style="font-weight: bold">${percentageFormatted}</span>`;
            const innerHtml = `
            <div style="overflow: show; border-radius: 10px; box-shadow: 0 6px 12px rgba(0,0,0,.175);">
                <div style="display: flex; flex-direction: column; padding: 10px; background-color: white; position: relative; border: 1px solid #D2D8DB; width: 250px; height: 76px">
                    <div style="display: flex;">
                        <div class="point" style="width: 10px; height: 10px; background-color: ${dataFromCurrentElement.dataset.backgroundColor[dataFromCurrentElement.dataIndex]}; border-radius: 50%; margin-right: 5px; margin-top: 5px"></div>
                        <div style="font-weight: bold; white-space: nowrap;">${metricName}</div>
                    </div>
                    <div style="display: flex; flex-direction: column; font-size: 13px;">
                        <div style="white-space: nowrap;">Evaluation count: ${boldMetricValue}</div>
                        <div>Percentage contribution: ${boldPercentage}</div>
                    </div>
                </div>
            </div>
        `;
            if (tooltipEl.querySelector('table')) {
                tooltipEl.querySelector('table').innerHTML = innerHtml;
            }
        }
        const position = context.chart.canvas.getBoundingClientRect();
        tooltipEl.style.opacity = '1';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - tooltipEl.clientHeight + 'px';
        tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
        tooltipEl.style.padding = '5px 5px';
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.zIndex = '1000';
    }
}
